import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  aprovarItem,
  aprovarTodos,
  fetchAprovacaoDetalhe,
  fetchFilaAprovacoes,
  reprovarItem,
  souGestor,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import { AprovacaoDetalhe, AprovacaoFilaItem } from '../../../interfaces/PortalCotacoes';
import ConfirmacaoModal from '../components/ConfirmacaoModal';
// As tags de estado dos itens usam as classes `pc-badge` — a folha e importada aqui em vez
// de contar com outro componente te-la carregado (a licao do BUG-V74/at-67).
import '../components/PortalBadges.css';
import { PORTAL_COTACAO_DETALHE, PORTAL_COTACOES } from '../routes';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de pagina compartilhado (recepcao-conte-do / -header-row / -title). Importado
// aqui em vez de contar com outra tela te-lo carregado antes (BUG-V74/at-67).
import '../../recepcao/pages/Recepcao.css';
import './PortalCotacoes.css';
import './PortalCotacaoDetalhe.css';

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que chega nesta fila',
    conteudo: (
      <p>
        Somente itens em que o comprador escolheu uma proposta <strong>acima do menor preço</strong>. A regra vale
        mesmo quando a escolha seguiu o score do sistema — o gatilho é sempre o preço, para que ninguém consiga
        contornar a supervisão ajustando os pesos.
      </p>
    ),
  },
  {
    titulo: 'Aprovar e reprovar',
    conteudo: (
      <p>
        A decisão é <strong>item a item</strong>. Aprovar libera a geração dos pedidos assim que todos os itens
        divergentes daquela cotação estiverem aprovados. Reprovar devolve <strong>só aquele item</strong> ao
        comprador com o seu comentário, mantendo as aprovações já dadas nos outros. No topo da tela,{' '}
        <strong>Aprovar escolhas do preset</strong> aprova de uma vez só os itens em que o comprador seguiu o
        preset; os escolhidos fora dele continuam pendentes para você olhar um a um.{' '}
        <strong>Aprovar todos</strong> aprova todos os pendentes, de qualquer origem.
      </p>
    ),
  },
  {
    titulo: 'Nada é gerado antes do veredito',
    conteudo: (
      <p>
        Enquanto houver item pendente, <strong>nenhum pedido é criado no ERP</strong> — nem os dos fornecedores sem
        divergência. Uma reprovação muda a composição da cesta, e pedidos parciais teriam de ser cancelados no ERP.
      </p>
    ),
  },
  {
    titulo: 'Como você fica sabendo que há item aqui',
    conteudo: (
      <p>
        Sempre que uma cesta é fechada com item acima do menor preço, todos os gestores recebem um aviso no{' '}
        <strong>sino da tela de cotações</strong>, com o número da cotação e quantos itens esperam decisão. O comprador é avisado do seu veredito do mesmo jeito — quando você reprova, ele recebe o seu
        comentário junto. Se a cotação for <strong>cancelada</strong> antes de você decidir, o aviso também
        chega: a pendência deixa de existir e você não fica procurando um item que sumiu da fila.
      </p>
    ),
  },
];

function moeda(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function tempoDesde(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const horas = Math.floor(ms / 3_600_000);
  if (horas < 1) return 'menos de 1 hora';
  if (horas < 24) return `${horas} hora${horas > 1 ? 's' : ''}`;
  const dias = Math.floor(horas / 24);
  return `${dias} dia${dias > 1 ? 's' : ''}`;
}

const PortalCotacoesAprovacoes: React.FC = () => {
  const navigate = useNavigate();
  const [fila, setFila] = useState<AprovacaoFilaItem[]>([]);
  const [detalhe, setDetalhe] = useState<AprovacaoDetalhe | null>(null);
  const [ehGestor, setEhGestor] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  const [comentarios, setComentarios] = useState<Record<string, string>>({});
  const [confirmacao, setConfirmacao] = useState<
    null | { tipo: 'aprovar' | 'reprovar'; aprovacaoId: string; descricaoItem: string } | { tipo: 'aprovar-todos' } | { tipo: 'aprovar-preset' }
  >(null);

  const carregarFila = useCallback(async () => {
    setCarregando(true);
    try {
      const [lista, gestor] = await Promise.all([fetchFilaAprovacoes(), souGestor()]);
      setFila(lista);
      setEhGestor(gestor.gestor);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarFila();
  }, [carregarFila]);

  /*
   * A fila se atualiza sozinha quando chega aviso do módulo (ADR 0005): o gestor com esta
   * tela aberta vê a pendência nova aparecer, e a linha sair quando a cotação é cancelada.
   *
   * ⚠️ Só estes dois tipos recarregam. Recarregar a fila a cada aviso faria a tela do
   * gestor piscar por causa de proposta recebida em cotação que nem passou por ele —
   * movimento que não muda nada aqui.
   */
  useEffect(() => {
    const aoReceberAviso = (evento: Event) => {
      const tipo = (evento as CustomEvent<{ tipo?: string }>).detail?.tipo;
      if (tipo === 'APROVACAO_PENDENTE' || tipo === 'COTACAO_CANCELADA') {
        carregarFila();
      }
    };
    window.addEventListener('portal:notificacao', aoReceberAviso);
    return () => window.removeEventListener('portal:notificacao', aoReceberAviso);
  }, [carregarFila]);

  /*
   * Colunas da fila no `TabelaSistema` (28/10/2026, pedido do Victor: a tabela "não está
   * seguindo os estilos das outras"). Era um `<table>` montado à mão, sem o casco das tabelas
   * do sistema. Memorizadas porque o `TabelaSistema` mede as colunas a cada troca do array; o
   * "Revisar" chama a função atual por ref, para não recriar as colunas a cada render.
   *
   * DOIS alvos clicáveis, com destinos diferentes (13/08/2026, decisão do Victor): o NÚMERO abre
   * a cotação em si, e "Revisar" abre a fila de aprovação daquela cotação, aqui mesmo.
   */
  const abrirDetalheRef = useRef<(id: string) => void>(() => undefined);
  const colunasDaFila = useMemo<ColunaTabela<AprovacaoFilaItem>[]>(
    () => [
      {
        titulo: 'Cotação',
        largura: 90,
        texto: (a) => a.numero,
        render: (a) => (
          <button
            type="button"
            className="pc-link-numero"
            title={`Abrir a cotação ${a.numero}`}
            onClick={() => navigate(PORTAL_COTACAO_DETALHE(a.cotacaoId))}
          >
            {a.numero}
          </button>
        ),
      },
      { titulo: 'Comprador', largura: 140, larguraMaxima: 240, texto: (a) => a.compradorNome ?? '—' },
      { titulo: 'Aguardando há', largura: 120, texto: (a) => tempoDesde(a.aguardandoDesde) },
      {
        titulo: 'Itens divergentes',
        largura: 120,
        texto: (a) => String(a.itensDivergentes),
        render: (a) => (
          <span style={{ color: 'var(--tema-texto-laranja, #b45309)', fontWeight: 500 }}>{a.itensDivergentes}</span>
        ),
      },
      {
        titulo: 'Custo da divergência',
        largura: 160,
        texto: (a) => `${moeda(a.custoDivergencia)} (+${a.custoDivergenciaPct}%)`,
        render: (a) => (
          <span>
            {moeda(a.custoDivergencia)} <span className="pc-texto-secundario">(+{a.custoDivergenciaPct}%)</span>
          </span>
        ),
      },
      {
        titulo: 'Ações',
        largura: 90,
        texto: () => 'Revisar',
        render: (a) => (
          <button
            type="button"
            className="pc-btn pc-btn--primario pc-btn--sm"
            onClick={() => abrirDetalheRef.current(a.cotacaoId)}
          >
            Revisar
          </button>
        ),
      },
    ],
    [navigate],
  );

  async function abrirDetalhe(cotacaoId: string) {
    try {
      setDetalhe(await fetchAprovacaoDetalhe(cotacaoId));
    } catch (error) {
      toast.error((error as Error).message);
    }
  }
  abrirDetalheRef.current = abrirDetalhe;

  async function executar(acao: () => Promise<unknown>, sucesso: string) {
    setProcessando(true);
    try {
      await acao();
      toast.success(sucesso);
      setConfirmacao(null);
      const cotacaoId = detalhe?.cotacaoId;
      await carregarFila();
      if (cotacaoId) {
        try {
          const atualizado = await fetchAprovacaoDetalhe(cotacaoId);
          const restam = atualizado.itensDivergentes.some((i) => i.statusAprovacao === 'PENDENTE');
          setDetalhe(restam ? atualizado : null);
        } catch {
          setDetalhe(null);
        }
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setProcessando(false);
    }
  }

  // ── Detalhe ────────────────────────────────────────────────────────────────

  if (detalhe) {
    const pendentes = detalhe.itensDivergentes.filter((i) => i.statusAprovacao === 'PENDENTE');
    // Pendentes cuja escolha seguiu o preset: o gestor pode aprová-los de uma vez (28/10/2026).
    const pendentesDoPreset = pendentes.filter((i) => i.seguiuMelhorScore);
    return (
      <div
        className="Frame1000004012"
        style={{
          height: 'fit-content',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          backgroundColor: 'transparent',
        }}
      >
        {/*
          `pc-page` fica junto do esqueleto compartilhado de proposito: ela nao da layout
          (nao existe regra de bloco para ela), mas e o ancestral que define a tipografia
          do Portal inteiro (`.pc-page h1, .pc-page td`...). Quem da margem e posicao e o
          `recepcao-conte-do`.
        */}
        <div className="ConteDo recepcao-conte-do pc-page">
          <div
            className="recepcao-header-row"
            style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
          >
            <div>
              <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Aprovação — {detalhe.numero}
                <button
                  type="button"
                  className="help-circle-btn"
                  title="Como Funciona"
                  onClick={() => setAjudaAberta(true)}
                >
                  ?
                </button>
              </div>
              <p className="pc-header-sub" style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7a99' }}>
                {detalhe.veiculo ?? ''} {detalhe.placa ? `· ${detalhe.placa}` : ''} · comprador{' '}
                {detalhe.compradorNome ?? '—'}
              </p>
            </div>
            <div className="pc-header-acoes">
              {ehGestor && pendentesDoPreset.length > 0 && (
                <button
                  type="button"
                  className="pc-btn pc-btn--sucesso"
                  onClick={() => setConfirmacao({ tipo: 'aprovar-preset' })}
                >
                  Aprovar escolhas do preset ({pendentesDoPreset.length})
                </button>
              )}
              {ehGestor && pendentes.length > 1 && (
                <button
                  type="button"
                  className="pc-btn pc-btn--primario"
                  onClick={() => setConfirmacao({ tipo: 'aprovar-todos' })}
                >
                  Aprovar todos ({pendentes.length})
                </button>
              )}
              <button type="button" className="pc-btn pc-btn--secundario" onClick={() => setDetalhe(null)}>
                Voltar à fila
              </button>
            </div>
          </div>

        {!ehGestor && (
          <div className="pc-aviso pc-aviso--atencao">
            Você está vendo esta tela em modo consulta. Só quem está cadastrado como gestor de compras pode aprovar
            ou reprovar.
          </div>
        )}

        {detalhe.itensDivergentes.length === 0 ? (
          <div className="pc-card">
            <div className="pc-vazio">Nenhum item divergente nesta cotação.</div>
          </div>
        ) : (
          detalhe.itensDivergentes.map((item) => (
            <div key={item.decisaoItemId} className="pc-card">
              <div className="pc-card-header">
                <h2 className="pc-card-titulo">{item.descricao}</h2>
                {/*
                  Estado da aprovação como TAG (pedido do Victor, 14/08/2026) — inclusive o
                  PENDENTE, que antes não mostrava nada: num detalhe com vários itens, o
                  card sem indicação nenhuma não dizia se aquele item aguardava o gestor ou
                  se já tinha sido resolvido. Mesmas classes de badge dos status da lista.
                */}
                {item.statusAprovacao === 'PENDENTE' && (
                  <span className="pc-badge pc-badge--laranja">
                    <span className="pc-badge-dot" />
                    Aguardando aprovação
                  </span>
                )}
                {item.statusAprovacao === 'APROVADA' && (
                  <span className="pc-badge pc-badge--verde">
                    <span className="pc-badge-dot" />
                    Aprovado
                  </span>
                )}
                {item.statusAprovacao === 'REPROVADA' && (
                  <span className="pc-badge pc-badge--vermelho">
                    <span className="pc-badge-dot" />
                    Reprovado
                  </span>
                )}
              </div>
              <div className="pc-card-corpo">
                <div className="pc-grid-2">
                  <div>
                    <p className="pc-resumo-rotulo">Escolhido pelo comprador</p>
                    <p style={{ margin: 0, fontWeight: 500 }}>{item.escolhida?.fornecedorNome}</p>
                    {/*
                      O valor escolhido usava `pc-numero-cotacao`, que é a classe do NÚMERO
                      da cotação: laranja, sublinhado e `cursor: pointer`. Um preço ficava
                      com cara de link sem ser clicável. Aqui o destaque é só cor e peso.
                    */}
                    <p className="pc-valor-destaque" style={{ margin: '2px 0', fontSize: 15 }}>
                      {moeda(item.escolhida?.totalItem ?? 0)}
                    </p>
                    <p className="pc-texto-secundario" style={{ margin: 0 }}>
                      {item.escolhida?.prazoEntregaDias === 0
                        ? 'Pronta entrega'
                        : `${item.escolhida?.prazoEntregaDias} dia(s)`}
                    </p>
                  </div>
                  <div>
                    <p className="pc-resumo-rotulo">Menor preço preterido</p>
                    <p style={{ margin: 0, fontWeight: 500 }}>{item.menorPrecoPreterido?.fornecedorNome}</p>
                    <p className="pc-valor" style={{ margin: '2px 0', fontSize: 15 }}>
                      {moeda(item.menorPrecoPreterido?.totalItem ?? 0)}
                    </p>
                    <p className="pc-texto-secundario" style={{ margin: 0 }}>
                      {item.menorPrecoPreterido?.prazoEntregaDias === 0
                        ? 'Pronta entrega'
                        : `${item.menorPrecoPreterido?.prazoEntregaDias} dia(s)`}
                    </p>
                  </div>
                </div>

                <p style={{ margin: '14px 0 0', fontSize: 13, color: 'var(--tema-texto-laranja, #b45309)' }}>
                  Diferença: <strong>{moeda(item.diferenca)}</strong> ({item.diferencaPct > 0 ? '+' : ''}
                  {item.diferencaPct}%)
                  {item.seguiuMelhorScore && ' — a escolha seguiu o melhor score do preset.'}
                </p>

                {item.justificativa && (
                  <div className="pc-resumo" style={{ marginTop: 12 }}>
                    <p className="pc-resumo-rotulo">Justificativa do comprador</p>
                    <p style={{ fontStyle: 'italic' }}>{item.justificativa}</p>
                  </div>
                )}

                {ehGestor && item.statusAprovacao === 'PENDENTE' && item.aprovacaoId && (
                  <>
                    <div className="pc-campo" style={{ marginTop: 14 }}>
                      <label className="pc-label" htmlFor={`com-${item.decisaoItemId}`}>
                        Comentário (obrigatório para reprovar)
                      </label>
                      <textarea
                        id={`com-${item.decisaoItemId}`}
                        className="pc-textarea"
                        rows={2}
                        value={comentarios[item.decisaoItemId] ?? ''}
                        onChange={(e) =>
                          setComentarios((c) => ({ ...c, [item.decisaoItemId]: e.target.value }))
                        }
                        placeholder="Explique ao comprador por que a escolha não se justifica"
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="pc-btn pc-btn--sucesso"
                        onClick={() =>
                          setConfirmacao({
                            tipo: 'aprovar',
                            aprovacaoId: item.aprovacaoId as string,
                            descricaoItem: item.descricao,
                          })
                        }
                      >
                        Aprovar item
                      </button>
                      <button
                        type="button"
                        className="pc-btn pc-btn--secundario"
                        style={{ color: 'var(--tema-texto-vermelho, #b42318)' }}
                        disabled={!(comentarios[item.decisaoItemId] ?? '').trim()}
                        onClick={() =>
                          setConfirmacao({
                            tipo: 'reprovar',
                            aprovacaoId: item.aprovacaoId as string,
                            descricaoItem: item.descricao,
                          })
                        }
                      >
                        Reprovar item
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))
        )}

        <ConfirmacaoModal
          isOpen={confirmacao?.tipo === 'aprovar'}
          titulo="Aprovar este item?"
          descricao={
            <>
              A escolha do comprador é mantida. Os pedidos serão gerados quando{' '}
              <strong>todos os itens divergentes</strong> desta cotação estiverem aprovados.
            </>
          }
          rotuloConfirmar="Aprovar"
          carregando={processando}
          onConfirmar={() =>
            confirmacao?.tipo === 'aprovar' &&
            executar(() => aprovarItem(confirmacao.aprovacaoId), 'Item aprovado.')
          }
          onCancelar={() => setConfirmacao(null)}
        />

        <ConfirmacaoModal
          isOpen={confirmacao?.tipo === 'reprovar'}
          titulo="Reprovar este item?"
          descricao={
            <>
              A escolha do comprador é <strong>desfeita apenas neste item</strong> e ele volta para a comparação com
              o seu comentário. As aprovações já dadas nos outros itens continuam valendo.
            </>
          }
          rotuloConfirmar="Reprovar"
          perigo
          carregando={processando}
          onConfirmar={() => {
            if (confirmacao?.tipo !== 'reprovar') return;
            const item = detalhe.itensDivergentes.find((i) => i.aprovacaoId === confirmacao.aprovacaoId);
            const comentario = item ? (comentarios[item.decisaoItemId] ?? '') : '';
            executar(() => reprovarItem(confirmacao.aprovacaoId, comentario), 'Item reprovado.');
          }}
          onCancelar={() => setConfirmacao(null)}
        />

        <ConfirmacaoModal
          isOpen={confirmacao?.tipo === 'aprovar-todos'}
          titulo="Aprovar todos os itens pendentes?"
          descricao={
            <>
              Os <strong>{pendentes.length} itens</strong> acima do menor preço serão aprovados de uma vez e os
              pedidos poderão ser gerados.
            </>
          }
          rotuloConfirmar="Aprovar todos"
          carregando={processando}
          onConfirmar={() => executar(() => aprovarTodos(detalhe.cotacaoId), 'Itens aprovados.')}
          onCancelar={() => setConfirmacao(null)}
        />

        <ConfirmacaoModal
          isOpen={confirmacao?.tipo === 'aprovar-preset'}
          titulo="Aprovar as escolhas do preset?"
          descricao={
            <>
              {pendentesDoPreset.length === 1 ? 'O item escolhido' : `Os ${pendentesDoPreset.length} itens escolhidos`}{' '}
              pelo preset serão aprovados de uma vez.{' '}
              {pendentes.length > pendentesDoPreset.length ? (
                <>
                  <strong>
                    {pendentes.length - pendentesDoPreset.length}{' '}
                    {pendentes.length - pendentesDoPreset.length === 1 ? 'item escolhido' : 'itens escolhidos'} fora do
                    preset
                  </strong>{' '}
                  {pendentes.length - pendentesDoPreset.length === 1 ? 'continua' : 'continuam'} pendente(s) para você
                  decidir um a um.
                </>
              ) : (
                'Com isso, os pedidos poderão ser gerados.'
              )}
            </>
          }
          rotuloConfirmar="Aprovar"
          carregando={processando}
          onConfirmar={() =>
            executar(() => aprovarTodos(detalhe.cotacaoId, { apenasPreset: true }), 'Escolhas do preset aprovadas.')
          }
          onCancelar={() => setConfirmacao(null)}
        />

          <ComoFunciona
            isOpen={ajudaAberta}
            onClose={() => setAjudaAberta(false)}
            titulo="Aprovações do gestor"
            secoes={helpSections}
          />
        </div>
      </div>
    );
  }

  // ── Fila ───────────────────────────────────────────────────────────────────

  return (
    <div
      className="Frame1000004012"
      style={{
        height: 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        backgroundColor: 'transparent',
      }}
    >
      <div className="ConteDo recepcao-conte-do pc-page">
        <div
          className="recepcao-header-row"
          style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
        >
          <div>
            <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Aprovações
              <button
                type="button"
                className="help-circle-btn"
                title="Como Funciona"
                onClick={() => setAjudaAberta(true)}
              >
                ?
              </button>
            </div>
            <p className="pc-header-sub" style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7a99' }}>
              Itens escolhidos acima do menor preço
            </p>
          </div>
          {/*
            Saída da tela (14/08/2026, pedido do Victor). A fila era um beco: sem link para
            a listagem, quem entrava dependia do menu ou do voltar do navegador. Mesmo botão
            branco da Supervisão — "Voltar à fila", logo acima, é outra coisa: aquele fecha o
            detalhe de uma aprovação e continua nesta tela.
          */}
          <div className="pc-header-acoes" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              className="CtaBig MouseHover"
              onClick={() => navigate(PORTAL_COTACOES())}
              style={{
                padding: '7px 16px',
                backgroundColor: 'var(--tema-superficie, white)',
                border: '1px solid var(--tema-linha-2, #CACACA)',
                cursor: 'pointer',
                marginRight: 0,
              }}
            >
              <div className="Frame1000003987" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={ArrowLeftOrange} alt="" width="16" height="12" />
                <span
                  style={{
                    color: 'var(--tema-texto, #1A1C1B)',
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: 'AmpleSoft-Bold, sans-serif',
                  }}
                >
                  Voltar à lista
                </span>
              </div>
            </div>
          </div>
        </div>

      {!ehGestor && !carregando && (
        <div className="pc-aviso pc-aviso--atencao">
          Você não está cadastrado como gestor de compras — consegue acompanhar a fila, mas não aprovar nem
          reprovar. O cadastro é feito por um administrador em Administração do Sistema, módulo Cotações.
        </div>
      )}

      <div className="pc-tabela-oficina-card">
        <TabelaSistema
          colunas={colunasDaFila}
          itens={fila}
          chave={(a) => a.cotacaoId}
          carregando={carregando}
          vazio="Nenhuma aprovação pendente."
        />
      </div>

        <ComoFunciona
          isOpen={ajudaAberta}
          onClose={() => setAjudaAberta(false)}
          titulo="Aprovações do gestor"
          secoes={helpSections}
        />
      </div>
    </div>
  );
};

export default PortalCotacoesAprovacoes;
